import { ThemePaletteContext } from '@/components/ThemeWrapper';
import { BookingDisplay } from '@/types';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Direction, ElevatorIcon, TDirection } from '@/data/direction';
import { formateTime } from '@/utils/formatTime';
import { formatDate } from '@/utils/formatDate';
import { getBookingListDisplay } from '@/api/getBookingListDisplay';
import { SignalRContext } from '@/providers/app';

function invertCssColor(color: string): string {
  const rgbaMatch = color.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)/i);
  if (rgbaMatch) {
    return `rgba(${255 - +rgbaMatch[1]},${255 - +rgbaMatch[2]},${255 - +rgbaMatch[3]},${rgbaMatch[4] ?? '1'})`;
  }
  const hexMatch = color.match(/^#([0-9a-f]{3,8})$/i);
  if (hexMatch) {
    const hex = hexMatch[1];
    if (hex.length === 3) {
      return `rgba(${255 - parseInt(hex[0] + hex[0], 16)},${255 - parseInt(hex[1] + hex[1], 16)},${255 - parseInt(hex[2] + hex[2], 16)},1)`;
    }
    return `rgba(${255 - parseInt(hex.slice(0, 2), 16)},${255 - parseInt(hex.slice(2, 4), 16)},${255 - parseInt(hex.slice(4, 6), 16)},1)`;
  }
  return color;
}

interface BookingListBlockProps {
  bookables: { bookableId: number; direction: TDirection; showElevator?: boolean }[];
  shouldCollapse: boolean;
  hideEndTime: boolean;
  rowCount: number;
  pageDuration: number;
  timeThreshold: number;
  forcedTimeThreshold: number;
  hideName: boolean;
  hideBookableName: boolean;
  hideExtra: boolean;
  hideTime: boolean;
  hideLocation: boolean;
  inverted?: boolean;
  paddingLeft?: number;
  paddingTop?: number;
  iconSize?: number;
}

type BookingRow = BookingDisplay & { direction: TDirection | 'null'; showElevator?: boolean };

function BookingListPageItem({
  bookables = [],
  rowCount: _rowCount,
  pageDuration = 10,
  shouldCollapse,
  hideName,
  hideBookableName,
  hideExtra,
  hideTime,
  hideLocation,
  hideEndTime,
  timeThreshold = 30,
  forcedTimeThreshold = 30,
  inverted = false,
  paddingLeft = 0,
  paddingTop = 0,
  iconSize = 80,
}: BookingListBlockProps) {
  const [currentTime, setCurrentTime] = useState(new Date().getTime());
  const [bookings, setBookings] = useState<{ [key: number]: BookingDisplay[] }>([]);
  const [pageNo, setPageNo] = useState(0);
  const { bookingRowBackground, bookingRowSecondaryBackground } = useContext(ThemePaletteContext);
  const textContentStyle = { paddingLeft, paddingTop, boxSizing: 'border-box' as const };

  const rowCount = shouldCollapse ? Object.entries(bookables).length : Number(_rowCount) ?? bookables.length;

  SignalRContext.useSignalREffect(
    'onBookingUpdated',
    (bookingItem: BookingDisplay) => {
      const bookable = bookings[bookingItem.bookableId];
      const booking = bookable.find(booking => booking.bookingItemId === bookingItem.bookingItemId);
      if (bookable && booking) {
        const index = bookable.indexOf(booking);
        bookable[index].startTime = bookingItem.startTime;
        bookable[index].endTime = bookingItem.endTime;
        bookable[index].name = bookingItem.name;
        bookable[index].location = bookingItem.location;
        bookable[index].extra = bookingItem.extra;
        bookable[index].imageUrl = bookingItem.imageUrl;
        setBookings({ ...bookings, [bookingItem.bookableId]: bookable });
      }
    },
    [bookings]
  );
  SignalRContext.useSignalREffect(
    'onRefetchBookings',
    () => {
      console.log('refetchBookings');
      getBookables();
    },
    [bookings]
  );
  SignalRContext.useSignalREffect(
    'onBookingRemoved',
    (bookingItemId: number) => {
      for (const key in bookings) {
        if (Object.prototype.hasOwnProperty.call(bookings, key)) {
          if (bookings[key].some(booking => booking.bookingItemId == bookingItemId)) {
            getBookables();
            break;
          }
        }
      }
    },
    [bookings]
  );
  const getBookables = async () => {
    try {
      const response = await getBookingListDisplay({
        bookableIds: bookables.map(({ bookableId }) => bookableId),
      });
      setBookings((response as any)?.data);
    } catch (error) {}
  };

  useEffect(() => {
    getBookables();

    const interval = setInterval(() => {
      getBookables();
    }, 15 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date().getTime());
    }, 15 * 1000);

    return () => clearInterval(interval);
  }, []);

  const rows = useMemo(() => {
    const timeThresholdMs = timeThreshold * 60 * 1000;
    const forcedTimeThresholdMs = forcedTimeThreshold * 60 * 1000;

    return bookables.reduce((acc: BookingRow[], { bookableId, direction, showElevator }) => {
      const bookableBookings = bookings[bookableId] ?? [];

      const filteredBookings = bookableBookings
        .filter(({ startTime, endTime }) => {
          const startTimeMs = +new Date(formatDate(startTime.toString()));
          const endTimeMs = +new Date(formatDate(endTime.toString()));
          return startTimeMs - new Date().getTime() <= timeThresholdMs && endTimeMs - new Date().getTime() >= 0;
        })
        .sort((a, b) => +new Date(formatDate(a.startTime.toString())) - +new Date(formatDate(b.startTime.toString())))
        .map(booking => ({
          ...booking,
          startTime: new Date(formatDate(booking.startTime.toString())),
          endTime: new Date(formatDate(booking.endTime.toString())),
        }));

      const finalBookings = filteredBookings.reduce((result: BookingRow[], currentBooking) => {
        if (result.length === 0 || +new Date(currentBooking.startTime?.toString()) - +new Date(result[result.length - 1].startTime?.toString()) > forcedTimeThresholdMs) {
          result.push({ ...currentBooking, direction, showElevator });
        } else {
          result[result.length - 1] = { ...currentBooking, direction, showElevator };
        }
        return result;
      }, []);

      if (finalBookings.length > 0) {
        acc.push({ ...finalBookings[0], direction, showElevator });
      }
      return acc;
    }, []);
  }, [bookings, bookables, timeThreshold, forcedTimeThreshold, currentTime]);

  const currentPageRows = rows.slice(pageNo * rowCount, (pageNo + 1) * rowCount);

  useEffect(() => {
    const pageCount = Math.ceil(rows.length / rowCount);

    if (pageCount <= 1) {
      setPageNo(0);
      return;
    }

    if (rowCount > 0 && pageCount > 1) {
      const interval = setInterval(() => {
        setPageNo(prev => {
          return ((prev + 1) % pageCount);
        });
      }, pageDuration * 1000);

      return () => clearInterval(interval);
    }
  }, [rowCount, pageDuration, rows.length]);

  return (
    <div className="booking-list-block h-full flex flex-col divide-y divide-input overflow-hidden" style={inverted ? { backgroundColor: invertCssColor(bookingRowBackground) } : undefined}>
      {currentPageRows.map((row, index) => {
        const originalRowBackground = index % 2 === 0 ? bookingRowBackground : bookingRowSecondaryBackground;
        const rowStyle = {
          ...(inverted
            ? { backgroundColor: index % 2 === 0 ? invertCssColor(bookingRowBackground) : invertCssColor(bookingRowSecondaryBackground) }
            : {}),
        };
        const rowTextStyle = inverted ? { color: originalRowBackground } : undefined;

        return (
          <div key={index} style={rowStyle} className="booking-row overflow-hidden">
            <div className={`flex w-full justify-between items-center pl-[2.5%] pr-[1%] ${index === 0 ? 'pb-[1.5%]' : 'py-[1.5%]'}`}>
              <div className="min-w-0 flex-1 flex flex-col leading-snug justify-center" style={textContentStyle}>
                <div className="w-full flex items-center justify-between">
                  <span className="booking-name truncate leading-tight" style={rowTextStyle}>
                    {!hideName && row.name}
                  </span>
                  {!hideTime && (
                    <span className="booking-time whitespace-nowrap ml-4" style={rowTextStyle}>
                      {row.startTime && formateTime(row.startTime.toString())}
                      {!hideEndTime &&
                        row.endTime &&
                        ` - ${formateTime(row.endTime.toString())}`}
                    </span>
                  )}
                </div>
                <div className="w-full flex items-center">
                  <span className="booking-extra truncate" style={rowTextStyle}>
                    {!hideBookableName && row.bookableName}
                    {!hideLocation && (
                      <>
                        {!hideBookableName && row.location && ', '}
                        <span className="booking-location" style={rowTextStyle}>{row.location}</span>
                      </>
                    )}
                    {!hideExtra && (
                      <>
                        {(!hideBookableName || !hideLocation) && row.extra && ' | '}
                        {row.extra}
                      </>
                    )}
                  </span>
                </div>
              </div>
              <div className="shrink-0 self-stretch w-[8%] flex justify-end items-center">
                <div className="h-full w-full flex items-center justify-end gap-[8%]" style={rowTextStyle}>
                  {row.showElevator && <ElevatorIcon className="text-[inherit]" style={{ height: `${iconSize}%`, width: 'auto', flexShrink: 0 }} />}
                  {row.direction !== 'null' && (
                    <Direction
                      direction={row.direction}
                      className="text-[inherit]"
                      style={{ height: `${iconSize}%`, width: 'auto', flexShrink: 0 }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default BookingListPageItem;
